import React, { useEffect, useState } from "react";
import { Mail, AlertTriangle, CheckCircle2, XCircle, Loader2, Send, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";

/**
 * Iter R — Email Delivery Health card + admin drilldown.
 *
 * Reads:
 *   GET  /api/admin/emails/health     — 24h rollup
 *   GET  /api/admin/emails/failures   — recent PII-sanitised failures
 *
 * Writes:
 *   POST /api/admin/emails/test       — send a live test email to verify config
 */

const STATUS_META = {
  sent:            { label: "Sent",              tone: "ok",   Icon: CheckCircle2 },
  failed:          { label: "Failed",            tone: "err",  Icon: XCircle },
  not_configured:  { label: "Not configured",    tone: "warn", Icon: AlertTriangle },
};

function toneCls(tone) {
  switch (tone) {
    case "ok":   return "border-emerald-700 bg-emerald-50 text-emerald-900";
    case "warn": return "border-amber-700 bg-amber-50 text-amber-900";
    case "err":  return "border-red-700 bg-red-50 text-red-900";
    default:     return "border-neutral-400 bg-neutral-50 text-neutral-700";
  }
}

function formatTs(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleString(undefined, {
      year: "numeric", month: "short", day: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
}

export function EmailHealthCard({ compact = false, onOpen }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/emails/health");
      setData(r.data);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Failed to load email health.");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  if (loading) return <div className="py-4 text-center text-sm text-[var(--smoke)]"><Loader2 size={14} className="inline animate-spin" /> Loading email health…</div>;
  if (!data) return null;

  const hasFailures = (data.failed || 0) > 0 || (data.not_configured || 0) > 0;
  const rate = data.success_rate === null ? "—" : `${(data.success_rate * 100).toFixed(1)}%`;

  return (
    <button
      type="button"
      onClick={onOpen}
      data-testid="email-health-card"
      className={`text-left w-full p-4 border-2 rounded-2xl transition hover:-translate-y-0.5 hover:shadow-[4px_4px_0_var(--ink)]
                  ${hasFailures ? "border-red-700 bg-red-50 ring-4 ring-red-600 ring-offset-2 ring-offset-[var(--paper)]"
                                : "border-[var(--ink)] bg-white"}`}
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Mail size={16} />
          <span className="text-xs uppercase tracking-widest font-bold">Email delivery · 24h</span>
        </div>
        {hasFailures && (
          <span className="text-[10px] uppercase tracking-widest font-bold bg-red-700 text-white px-1.5 py-0.5 rounded">
            Action
          </span>
        )}
      </div>
      <div className="mt-3 flex items-baseline gap-3">
        <div className="font-display text-4xl leading-none" data-testid="email-health-total">{data.total}</div>
        <div className="text-xs text-[var(--smoke)]">
          <div><span className="font-bold text-emerald-700" data-testid="email-health-sent">{data.sent}</span> sent</div>
          <div><span className="font-bold text-red-700" data-testid="email-health-failed">{data.failed}</span> failed</div>
        </div>
      </div>
      <div className="mt-2 flex items-center justify-between text-[11px] text-[var(--smoke)]">
        <span>Success rate: <b>{rate}</b></span>
        {!data.resend_configured && (
          <span className="text-red-700 font-bold" data-testid="email-health-not-configured">
            RESEND_API_KEY missing
          </span>
        )}
        {data.resend_configured && !data.sender_configured && (
          <span className="text-red-700 font-bold" data-testid="email-health-sender-missing">
            Email sender not configured
          </span>
        )}
      </div>
    </button>
  );
}

export default function EmailsAdmin() {
  const [health, setHealth] = useState(null);
  const [failures, setFailures] = useState([]);
  const [deliveries, setDeliveries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [testTo, setTestTo] = useState("");
  const [sending, setSending] = useState(false);
  const [tab, setTab] = useState("failures");

  const load = async () => {
    setLoading(true);
    try {
      const [h, f, d] = await Promise.all([
        api.get("/admin/emails/health"),
        api.get("/admin/emails/failures", { params: { limit: 50 } }),
        api.get("/admin/emails/deliveries", { params: { limit: 50 } }),
      ]);
      setHealth(h.data);
      setFailures(f.data.failures || []);
      setDeliveries(d.data.deliveries || []);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Failed to load email data.");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const sendTest = async () => {
    const to = (testTo || "").trim();
    if (!to) return toast.error("Enter a recipient email.");
    setSending(true);
    try {
      const r = await api.post("/admin/emails/test", { to });
      const d = r?.data || {};
      if (d.ok) {
        toast.success(`Test email dispatched to ${to}`);
      } else {
        // Iter R — the endpoint returns HTTP 200 with ok:false + reason so
        // the admin can see the true failure cause (not_configured / permanent /
        // transient_exhausted) instead of a generic proxy 5xx.
        toast.error(
          d.reason === "not_configured"
            ? "RESEND_API_KEY is not set on the backend. Set it in production env vars and redeploy."
            : (d.message || `Test send failed: ${d.reason || "unknown reason"}`)
        );
      }
      await load();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Test send failed. Check Failures below.");
      await load();
    } finally { setSending(false); }
  };

  if (loading && !health) return <div className="py-8 text-center"><Loader2 size={16} className="inline animate-spin" /> Loading…</div>;

  const rate = health?.success_rate === null || health?.success_rate === undefined
    ? "—"
    : `${(health.success_rate * 100).toFixed(1)}%`;
  const rows = tab === "failures" ? failures : deliveries;

  return (
    <div className="space-y-6" data-testid="emails-admin">
      {/* Config + health summary */}
      <section className="border-2 border-[var(--ink)] rounded-2xl bg-white p-5 shadow-[6px_6px_0_var(--ink)]">
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="kicker kicker-red">Delivery health</div>
            <h2 className="font-display text-2xl">Email observability</h2>
          </div>
          <button onClick={load} className="btn-ghost text-sm" data-testid="emails-refresh">
            <RefreshCw size={14} /> Refresh
          </button>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <Metric label="Sent (24h)" value={health?.sent ?? 0} tone="ok" testid="metric-sent" />
          <Metric label="Failed (24h)" value={health?.failed ?? 0} tone={health?.failed ? "err" : "mute"} testid="metric-failed" />
          <Metric label="Not configured" value={health?.not_configured ?? 0} tone={health?.not_configured ? "warn" : "mute"} testid="metric-notconfig" />
          <Metric label="Success rate" value={rate} tone={health?.failed ? "warn" : "ok"} testid="metric-rate" />
          <Metric label="Total (24h)" value={health?.total ?? 0} tone="mute" testid="metric-total" />
        </div>
        {!health?.resend_configured && (
          <div className="mt-4 border-2 border-red-700 bg-red-50 rounded-lg p-3 text-sm text-red-900" data-testid="resend-warning">
            <b>RESEND_API_KEY is not set on the backend.</b> Every email is currently recorded as
            <code> not_configured</code> and never leaves the server. Set the env var and redeploy to enable delivery.
          </div>
        )}
        {!health?.sender_configured && (
          <div className="mt-3 border-2 border-red-700 bg-red-50 rounded-lg p-3 text-sm text-red-900" data-testid="sender-warning">
            <b>Email sender not configured.</b> <code>SENDER_EMAIL</code> is unset — the app refuses to fall back to a
            placeholder domain. Set <code>SENDER_EMAIL</code> (e.g. <code>orders@collectorup.com</code>) and
            <code>SENDER_NAME</code> on the backend and redeploy to enable delivery.
          </div>
        )}
        <div className="mt-3 text-[11px] text-[var(--smoke)]">
          Sender: <b>{health?.sender_name || "—"}</b> &lt;<b>{health?.sender_email || "(unset)"}</b>&gt;
        </div>
      </section>

      {/* Test send */}
      <section className="border-2 border-[var(--ink)] rounded-2xl bg-white p-5 shadow-[6px_6px_0_var(--ink)]" data-testid="test-send-panel">
        <div className="kicker kicker-black">Verify configuration</div>
        <h3 className="font-display text-lg mb-1">Send a test email</h3>
        <p className="text-sm text-[var(--smoke)] mb-3">
          Dispatches a branded transactional email to any address so you can confirm Resend, DNS, and
          SPF/DKIM are correctly wired before launch. The message is fully audited on this page.
        </p>
        <div className="flex flex-col sm:flex-row gap-2">
          <input
            type="email"
            value={testTo}
            onChange={(e) => setTestTo(e.target.value)}
            placeholder="gil@collectorup.com"
            className="gm-field flex-1"
            data-testid="test-send-input"
          />
          <button
            onClick={sendTest}
            disabled={sending}
            className="btn-red flex items-center gap-1 disabled:opacity-60"
            data-testid="test-send-btn"
          >
            {sending ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
            {sending ? "Sending…" : "Send test"}
          </button>
        </div>
      </section>

      {/* Per-template breakdown */}
      {health?.by_template?.length > 0 && (
        <section className="border-2 border-[var(--ink)] rounded-2xl bg-white p-5 shadow-[6px_6px_0_var(--ink)]" data-testid="by-template-panel">
          <h3 className="font-display text-lg mb-3">By template (24h)</h3>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
            {health.by_template.map((r) => (
              <div key={r.template_id} className="border rounded-lg p-2 text-xs" data-testid={`tpl-${r.template_id}`}>
                <div className="font-mono font-bold text-[var(--ink)] truncate" title={r.template_id}>{r.template_id}</div>
                <div className="mt-1 flex gap-2">
                  <span className="text-emerald-700">{r.sent} sent</span>
                  {r.failed > 0 && <span className="text-red-700">{r.failed} failed</span>}
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Deliveries list */}
      <section className="border-2 border-[var(--ink)] rounded-2xl bg-white p-5 shadow-[6px_6px_0_var(--ink)]">
        <div className="flex items-center gap-2 mb-3">
          <button
            onClick={() => setTab("failures")}
            className={`px-3 py-1 text-xs uppercase tracking-widest font-bold rounded ${tab === "failures" ? "bg-[var(--ink)] text-white" : "text-[var(--smoke)] hover:text-[var(--ink)]"}`}
            data-testid="tab-failures"
          >Failures ({failures.length})</button>
          <button
            onClick={() => setTab("all")}
            className={`px-3 py-1 text-xs uppercase tracking-widest font-bold rounded ${tab === "all" ? "bg-[var(--ink)] text-white" : "text-[var(--smoke)] hover:text-[var(--ink)]"}`}
            data-testid="tab-all"
          >All ({deliveries.length})</button>
        </div>
        {rows.length === 0 ? (
          <div className="py-6 text-center text-sm text-[var(--smoke)]" data-testid="emails-empty">
            No records to show.
          </div>
        ) : (
          <ul className="space-y-2 max-h-[560px] overflow-auto pr-1">
            {rows.map((r) => {
              const meta = STATUS_META[r.status] || { label: r.status, tone: "mute", Icon: Mail };
              const Icon = meta.Icon;
              return (
                <li key={r.delivery_id} className="border border-neutral-200 rounded-lg p-2 text-xs" data-testid={`delivery-${r.delivery_id}`}>
                  <div className="flex items-center justify-between gap-2 flex-wrap">
                    <div className="flex items-center gap-2 min-w-0">
                      <span className={`inline-flex shrink-0 items-center gap-1 border rounded-full px-1.5 py-0.5 font-bold ${toneCls(meta.tone)}`}>
                        <Icon size={10} /> {meta.label}
                      </span>
                      <span className="font-mono text-[var(--ink)] break-all">{r.to}</span>
                      <span className="text-[var(--smoke)] font-mono">[{r.template_id}]</span>
                    </div>
                    <span className="text-[var(--smoke)]">{formatTs(r.completed_at || r.created_at)}</span>
                  </div>
                  {r.subject && <div className="mt-1 text-[var(--smoke)] truncate">{r.subject}</div>}
                  {r.error && (
                    <div className="mt-1 text-red-800 italic truncate" title={r.error}>
                      {r.failure_kind ? `[${r.failure_kind}] ` : ""}{r.error}
                    </div>
                  )}
                  {r.attempts > 1 && <div className="mt-1 text-[var(--smoke)]">Attempts: {r.attempts}</div>}
                </li>
              );
            })}
          </ul>
        )}
      </section>
    </div>
  );
}

function Metric({ label, value, tone, testid }) {
  return (
    <div className={`border-2 rounded-lg p-3 ${toneCls(tone)}`} data-testid={testid}>
      <div className="text-[10px] uppercase tracking-widest font-bold opacity-80">{label}</div>
      <div className="font-display text-2xl mt-1 leading-none">{value}</div>
    </div>
  );
}
